import { CategoryDisplayName } from '@oxappsec/ox-consolidated-categories';
import {
  AggregationItem,
  Nullable,
  SeverityType,
  TicketRef,
} from 'common-types';
import { IconType } from 'common-types';
import {
  FilterElements,
  FilterType,
  FilterTypeSearch,
  FilterTypeToFilter,
} from '../../../common/filters-utils/filters-utils';
import { RepoTypes } from 'common-types';
import { ApplicationFlow } from '../../../elements/AppFlow/types/app-flow-types';
import { RawIssue, RawIssueApp, RawIssuePolicy } from './raw-issue-type';

export enum IssueSortKey {
  Category = 'Category',
  IssueName = 'IssueName',
  RepoName = 'RepoName',
  Owner = 'Owner',
  OpenDate = 'OpenDate',
  Occurrences = 'Occurrences',
  ResolvedDate = 'ResolvedDate',
  Severity = 'Severity',
  JobTriggeredAt = 'JobTriggeredAt',
  JobTriggeredBy = 'JobTriggeredBy',
  CicdIssueStatus = 'CicdIssueStatus',
  Enforcement = 'Enforcement',
  JobId = 'JobId',
}

export interface Filter {
  header: FilterType;
  type: FilterTypeToFilter;
  searchType?: FilterTypeSearch;
}

export interface IssueDetails {
  id: string;
  issueId: string;
  name: string;
  mainTitle: string;
  secondTitle: string;
  secondTitleMarkup: string;
  severity: SeverityType;
  owners: string[];
  connector: string;
  learnMore: string[];
  app: RawIssueApp;
  policy: RawIssuePolicy;
  exclusions: Exclusions[];
  recommendation: string;
  recommendationMarkup: string;
  violationInfoTitle: string;
  sourceTools: string[];
  isPRAvailable: boolean;
  aggregations: {
    type: string;
    items: AggregationItem[];
  };
  ruleId: string;
  exclusionCategory: string;
  resource: {
    id: string;
    type: string;
  };
  cwe: string[];
  extraInfo: [{ key: string; value: string }];
  applicationFlow: ApplicationFlow;
  fixLink: string;
  cweList: CweList[];
  category: CategoryDeatils;
  exclusionIssueComment: string;
  exclusionAppComment: string;
  exclusionPolicyComment: string;
  dependencyChain: string[];
  publicExploitLink: string;
  fixIssue: Nullable<FixIssueDetails>;
  lowerSeverityReason: string[];
  scaVulnerabilities: ScaVulnerability[];
  directSCAVulnerability: ScaVulnerability[];
  noneDirectSCAVulnerability: ScaVulnerability[];
  dependencyGraphNodes: DependencyNode[];
  dependencyGraphEdges: DependencyEdge[];
  severityChangedReason: SeverityChangedReason[];
  originalToolSeverity: SeverityType;
  cicdFields?: CiCdFields;
  severityBreakdown: SeverityBreakdownItem[];
  occurrences: number;
  created: Date;
  comment: string;
  tickets: JiraTicketRef[];
  excludedByAlert: boolean;
  fixes: PolicyFix;
  snoozeExpiredDate?: Date;
  exclusionId: string;
  isMonoRepoChild: boolean;
  monoRepoParent: string;
}

export interface SlackNotification {
  channel: string;
  timestamp: string;
}
export interface SeverityBreakdownItem {
  trend: SeverityTrend;
  itemsCount: number;
  name: string;
  description: string;
  changeNumber: number;
  changeCategory: string;
}

export enum SeverityTrend {
  Up = 'up',
  Down = 'down',
  Neutral = 'neutral',
}

export interface SeverityChangedReason {
  changeNumber: number;
  reason: string;
  shortName: string;
  changeCategory: string;
}

export interface DependencyNode {
  position?: { x: number; y: number };
  id: string;
  name?: string;
  vulnerable: boolean;
  issues: {
    appox: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

export interface DependencyEdge {
  v: string;
  w: string;
}
export interface ScaVulnerability {
  cwe: { url: string; name: string; shortName: string }[];
  cve: string;
  libName: string;
  dependencyChain: string;
  libVersion: string;
  chainDepth: string;
  cveLink: string;
  cvsVer: string;
  exploitInTheWildLink: string;
  description: string;
  dateDiscovered: string;
  minorVerWithFix: string;
  majorVerWithFix: string;
  exploitRequirement: string;
  exploitCode: ExploitCode;
  originalSeverity: string;
}

export enum ExploitCode {
  Local = 'LOCAL',
  Network = 'NETWORK',
}
export interface FixIssue {
  fixType: FixType;
  activeFix: Nullable<ActiveFix>;
}

export interface FixIssueDetails extends FixIssue {
  fixPR: FixPR;
}

export interface ActiveFix {
  fixId: string;
  fixURL: string;
}

export interface FixPR {
  issueBranch: string;
  commitMessage: string;
  fixFiles: FixFile[];
}

export interface FixFile {
  filePath: string;
  newFileContent: string;
}

export enum FixType {
  PullRequest = 'pullRequest',
}
export interface CweList {
  name: string;
  description: string;
  url: string;
}

export interface RepoInfo {
  businessPriority: number;
  scores?: ScoreItem[];
}

export interface PolicyInfo {
  info: string;
  severity: SeverityType;
  careReason: string;
}

export interface Issue {
  id: string;
  index: number;
  issueId: string;
  category: CategoryDisplayName;
  categoryId: string;
  description: string;
  mainTitle: string;
  secondTitle: string;
  isPRAvailable: boolean;
  repo: {
    type: RepoTypes;
    name: string;
  };
  fix: PolicyFix;
  owner: string[];
  createdDate: Date;
  scanCreatedDate: Date;
  occurrences: number;
  exclusions: Exclusions[];
  app: RawIssueApp;
  fixIssue: Nullable<FixIssue>;
  jiraTickets: JiraTicketRef[];
  resolvedIssueDate?: Date;
  cicdFields?: CiCdFields;
  severity: SeverityType;
  comment: string;
  slackNotification?: SlackNotification[];
  excludedByAlert: boolean;
  fixes: PolicyFix;
  policyId: string;
}

export type JiraTicketRef = Omit<TicketRef, 'provider'>;

export interface FetchIssuesInput {
  scanID?: string;
  offset?: number;
  limit?: number;
  filters?: {};
  sort?: {
    fields: Nullable<IssueSortKey>[];
    order: string[];
  };
  updateFilter?: boolean;
  owners?: string[];
  inventoryFilters?: string[];
  dateRange?: {};
  search?: SearchInput[];
}

export interface FetchIssueInput {
  issueId: string;
}

export interface FetchIssuesResponse {
  getIssues: FetchIssuesData;
  getSingleIssueInfo: IssueDetails;
  getIssuesFilters: FilterIssueType;
}
export interface FetchResolvedIssuesResponse {
  getResolvedIssues: FetchIssuesData;
}
export interface FetchResolvedSingleIssueResponse {
  getResolvedIssue: IssueDetails;
}

export interface FetchPipelineIssuesResponse {
  getCICDIssues: FetchIssuesData;
}

export interface CiCdFields {
  issueStatus: string;
  jobId: string;
  jobUrl: string;
  jobTriggeredAt: Date;
  jobTriggeredBy: string;
  jobTriggeredReason: string;
  sourceBranch: string;
  targetBranch: string;
  pullRequestId: string;
  pullRequestUrl: string;
  enforcement: Enforcement;
}

export enum Enforcement {
  Monitor = 'Monitor',
  Block = 'Block',
}
export interface FetchPipelineSingleIssueResponse {
  getCICDIssue: IssueDetails;
}

export interface FetchResolvedIssuesFiltersResponse {
  getResolvedIssuesFilters: FilterIssueType;
}

export interface FetchPipelineIssuesFiltersResponse {
  getCICDIssuesFilters: FilterIssueType;
}

export interface FilterIssueType {
  apps: FilterElements[];
  criticality: FilterElements[];
  fixedIssues: FilterElements[];
  categories: FilterElements[];
  policies: FilterElements[];
  issueOwners: FilterElements[];
  issueNames: FilterElements[];
  sourceTools: FilterElements[];
  cwe: FilterElements[];
  issueStatus: FilterElements[];
  uniqueLibs: FilterElements[];
  filePaths: FilterElements[];
}
export interface FetchIssuesData {
  issues: RawIssue[];
  totalIssues: number;
  offset: number;
  totalFilteredIssues: number;
  totalResolvedIssues: number;
  totalActiveIssues: number;
}

export interface ScoreItem {
  severityType: SeverityType;
  value: number | string;
  content?: string;
}

export interface AdditionalInfo {
  exploitWild: boolean;
  exploitType: string;
}

export interface Exclusions {
  label: string;
  type: string;
  id: string;
  isDefault: boolean;
}

export interface learnMoreLink {
  name: string;
  href: string;
}

// Perhaps we can define it without the indexer
export interface AppFlowItemTooltipsProp {
  [key: string]: {
    title: string;
    content: string[];
    link?: string;
    icon?: IconType;
  };
}

export enum IssuesOrder {
  Asc = 'ASC',
  Desc = 'DESC',
}

export interface FilterByTypesToolTip {
  category: string;
  value: string;
}

export interface DisablePolicyStatus {
  success: boolean;
  error: boolean;
}

export interface ExclusionItem {
  label: string;
  type: string;
  id: string;
  isDefault: boolean;
}

export interface CategoryDeatils {
  name: string;
  categoryId: string;
}

export enum ExtraInfoTypes {
  OriginalSeverity = 'Original Severity',
  LowerSeverityReason = 'Lower Severity Reason',
  RecommendedVideo = 'Recommended Video',
}

export type LoadIssuesParams = {
  update?: boolean;
  cache?: boolean;
  limit?: number;
  issueId?: Nullable<string>;
  exclude?: boolean;
};

export type LoadIssuesFiltersParams = {
  cache?: boolean;
  limit?: number;
  search?: SearchInput[];
  offset?: number;
};

export interface AddCommentToIssueResponse {
  isUpdated: boolean;
}

export interface AddCommentToIssueInput {
  issueId: string;
  comment: string;
}

export interface IssueAction {
  text: string;
  icon: IconType;
  onClick: () => void;
  disabled?: boolean;
  highlight: boolean;
}

export interface PolicyFixInput {
  type: string;
  name: string;
  displayName?: string;
  minSelect?: number;
  maxSelect?: number;
  options: PolicyFixInputOption[];
  multiSelect?: boolean;
}
export interface PolicyFixInputOption {
  name: string;
  displayName?: string;
  selected?: boolean;
  metadata: string;
  info?: string;
}
export interface PolicyFix {
  settingType: string;
  tooltip: string;
  description: string;
  warning: string;
  confirmation: string;
  inputs: PolicyFixInput[];
}

export interface SeverityByCategory {
  changeCategory: string;
  severities: SeverityBreakdownItem[];
}

export enum SeverityChangeFactorTypes {
  Reachability = 'Reachable',
  Exploitability = 'Exploitable',
  Damage = 'Damage',
}

export interface SearchInput {
  fieldName: string;
  value: string;
}
